/**
 * @module Component
 */
import React, { ReactNode } from 'react';
import classNames from 'classnames';

/**
 * This interface is referencing the Avatar component props
 * @category Component
 */
export interface AvatarProps {
  /**
   * The testId is string.
   */
  testId?: string;
  /**
   * The testId is string.
   */
  size?: 'xs' | 'sm' | 'xm' | 'md' | 'lg';
  /**
   * The size is string.
   */
  status?: ReactNode | string;
  /**
   * The status is react node or string.
   */
  imageURL?: string;
  /**
   * The imageURL is string.
   */
  name?: string;
  /**
   * The name is string.
   */
}

/**
 *
 * @param AvatarProps
 * @returns this will return avatar Component
 * @category Component
 */
const Avatar = ({
  testId,
  size = 'md',
  status,
  imageURL,
  name,
}: AvatarProps) => {
  const avatarInitial = name ? name.charAt(0).toUpperCase() : 'A';

  /**
   *
   * @returns
   */
  const getImageAvatar = (): ReactNode => (
    <img
      src={imageURL}
      alt={name || 'unknown'}
      className={classNames('rounded-circle', 'bg-light', 'd-block', {
        [`avatar-${size}`]: size,
      })}
    />
  );

  /**
   *
   * @returns
   */
  const getAlphabeticAvatar = (): ReactNode => (
    <div
      className={classNames(
        'rounded-circle',
        'd-flex',
        'justify-content-center',
        'align-items-center',
        'bg-dark',
        'text-light',
        {
          [`avatar-${size}`]: size,
          [`display-${size}`]: size,
        },
      )}
    >
      {avatarInitial}
    </div>
  );

  //Avatar Component
  const getAvatar = (): ReactNode =>
    imageURL ? getImageAvatar() : getAlphabeticAvatar();

  /*STATUS */

  /**
   *
   * @returns
   */
  const getStringStatus = (): ReactNode => (
    <div
      className={classNames(
        'bg-success',
        'position-absolute',
        'rounded-circle',
        'border',
        'border-white',
        {
          [`avatar-status-${size}`]: size,
          'border-1': size === 'xs' || size === 'sm',
          'border-2': size === 'xm' || size === 'md',
          'border-3': size === 'lg',
        },
      )}
    />
  );

  /**
   *
   * @returns
   */
  const getIconStatus = (): ReactNode => (
    <div
      className={classNames('position-absolute d-flex', {
        [`avatar-${size}-icon`]: size,
      })}
    >
      {status}
    </div>
  );

  /**
   *
   * @returns
   */
  const getStatus = (): ReactNode => {
    if (status)
      return typeof status === 'string' ? getStringStatus() : getIconStatus();
    else <></>;
  };

  return (
    <div className="avatar position-relative" data-testid={testId}>
      {getAvatar()}
      {getStatus()}
    </div>
  );
};

export default Avatar;
